<script setup>
import { onMounted, computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useRoute, useRouter } from 'vue-router';
import { useCompaniesStore } from 'dashboard/stores/companies';
import { useCompanyNotesStore } from 'dashboard/stores/companyNotes';
import { useCompanyContactsStore } from 'dashboard/stores/companyContacts';

import CompanyDetailsLayout from 'dashboard/components-next/Companies/CompanyDetailsLayout.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import CompanyDetails from 'dashboard/components-next/Companies/Pages/CompanyDetails.vue';
import TabBar from 'dashboard/components-next/tabbar/TabBar.vue';
import CompanyNotes from 'dashboard/components-next/Companies/CompanySidebar/CompanyNotes.vue';
import CompanyContacts from 'dashboard/components-next/Companies/CompanySidebar/CompanyContacts.vue';
import CompanyCustomAttributes from 'dashboard/components-next/Companies/CompanySidebar/CompanyCustomAttributes.vue';
import CompanyHistory from 'dashboard/components-next/Companies/CompanySidebar/CompanyHistory.vue';

const route = useRoute();
const router = useRouter();
const companiesStore = useCompaniesStore();
const companyNotesStore = useCompanyNotesStore();
const companyContactsStore = useCompanyContactsStore();
const { t } = useI18n();

const activeTab = ref('attributes');
const hasError = ref(false);

const uiFlags = computed(() => companiesStore.getUIFlags);
const selectedCompany = computed(() => companiesStore.getSelectedCompany);

const isFetchingItem = computed(() => uiFlags.value.fetchingItem);
const isUpdating = computed(() => uiFlags.value.updating);

const companyId = computed(() => parseInt(route.params.companyId));

const COMPANY_TABS_OPTIONS = [
  { key: 'ATTRIBUTES', value: 'attributes' },
  { key: 'HISTORY', value: 'history' },
  { key: 'NOTES', value: 'notes' },
  { key: 'CONTACTS', value: 'contacts' },
];

const tabs = computed(() => {
  return COMPANY_TABS_OPTIONS.map(tab => ({
    label: t(`COMPANIES.TABS.${tab.key}`),
    value: tab.value,
  }));
});

const activeTabIndex = computed(() => {
  return COMPANY_TABS_OPTIONS.findIndex(v => v.value === activeTab.value);
});

const goToCompaniesList = () => {
  if (window.history.state?.back || window.history.length > 1) {
    router.back();
  } else {
    router.push(`/app/accounts/${route.params.accountId}/companies`);
  }
};

const fetchActiveCompany = async () => {
  if (!companyId.value) return;
  hasError.value = false;
  try {
    await companiesStore.show(companyId.value);
  } catch (error) {
    hasError.value = true;
    console.error('Failed to fetch company:', error);
  }
};

const fetchCompanyNotes = () => {
  if (companyId.value) {
    companyNotesStore.get(companyId.value);
  }
};

const fetchCompanyContacts = () => {
  if (companyId.value) {
    companyContactsStore.get(companyId.value);
  }
};

const handleTabChange = tab => {
  activeTab.value = tab.value;
};

const handleUpdateCompany = async companyData => {
  try {
    await companiesStore.update(companyId.value, companyData);
    useAlert(t('COMPANIES.FORM.UPDATE_SUCCESS'));
  } catch (error) {
    useAlert(t('COMPANIES.FORM.UPDATE_ERROR'));
  }
};

const handleDeleteCompany = async () => {
  try {
    await companiesStore.delete(companyId.value);
    useAlert(t('COMPANIES.DELETE_SUCCESS'));
    goToCompaniesList();
  } catch (error) {
    useAlert(t('COMPANIES.DELETE_ERROR'));
  }
};

const handleAvatarUpload = async ({ file }) => {
  try {
    await companiesStore.update(companyId.value, { avatar: file });
    useAlert(t('COMPANIES.AVATAR_UPLOAD_SUCCESS'));
  } catch (error) {
    useAlert(t('COMPANIES.AVATAR_UPLOAD_ERROR'));
  }
};

const handleAvatarDelete = async () => {
  try {
    useAlert(t('COMPANIES.AVATAR_DELETE_SUCCESS'));
  } catch (error) {
    useAlert(t('COMPANIES.AVATAR_DELETE_ERROR'));
  }
};

// Refetch when companyId changes
watch(companyId, () => {
  fetchActiveCompany();
});

onMounted(() => {
  fetchActiveCompany();
  fetchCompanyNotes();
  fetchCompanyContacts();
});
</script>

<template>
  <div
    class="flex flex-col justify-between flex-1 h-full m-0 overflow-auto bg-n-background"
  >
    <!-- Loading State -->
    <div
      v-if="isFetchingItem"
      class="flex items-center justify-center flex-1 h-full"
    >
      <Spinner />
    </div>

    <!-- Error State -->
    <div
      v-else-if="hasError"
      class="flex flex-col items-center justify-center flex-1 h-full gap-4"
    >
      <span class="i-lucide-alert-circle text-n-ruby-9 size-12" />
      <p class="text-n-slate-11">{{ t('COMPANIES.LOAD_ERROR') }}</p>
      <Button
        :label="t('COMPANIES.RETRY')"
        @click="fetchActiveCompany"
      />
    </div>

    <!-- Content -->
    <CompanyDetailsLayout
      v-else-if="selectedCompany"
      :selected-company="selectedCompany"
      :is-updating="isUpdating"
      @go-to-companies-list="goToCompaniesList"
    >
      <CompanyDetails
        :company="selectedCompany"
        :is-updating="isUpdating"
        @update="handleUpdateCompany"
        @delete="handleDeleteCompany"
        @avatar-upload="handleAvatarUpload"
        @avatar-delete="handleAvatarDelete"
      />
      <template #sidebar>
        <div class="px-6">
          <TabBar
            :tabs="tabs"
            :initial-active-tab="activeTabIndex"
            class="w-full [&>button]:w-full bg-n-alpha-black2"
            @tab-changed="handleTabChange"
          />
        </div>
        <CompanyCustomAttributes
          v-if="activeTab === 'attributes'"
          :selected-company="selectedCompany"
        />
        <CompanyHistory
          v-if="activeTab === 'history'"
          :company-id="companyId"
        />
        <CompanyNotes
          v-if="activeTab === 'notes'"
          :company-id="companyId"
        />
        <CompanyContacts
          v-if="activeTab === 'contacts'"
          :company-id="companyId"
        />
      </template>
    </CompanyDetailsLayout>

    <!-- Not Found State -->
    <div
      v-else
      class="flex flex-col items-center justify-center flex-1 h-full gap-4"
    >
      <span class="i-lucide-building-2 text-n-slate-9 size-12" />
      <p class="text-n-slate-11">{{ t('COMPANIES.NOT_FOUND') }}</p>
      <Button
        :label="t('COMPANIES.GO_BACK')"
        @click="goToCompaniesList"
      />
    </div>
  </div>
</template>
